import { useMemo } from 'react';
import BigNumber from 'bignumber.js';
import { Button, useQuery, useI18n } from '@ywwwtseng/react-kit';
import { Typography, useShare } from '@ywwwtseng/tma-react-kit';
import { useShareUrl } from '@/hooks/useShareUrl';
import { MOONINI_DECIMALS } from '@/constants';
import type { Gift } from '@/types';

export default function ShareGiftScreen({
  params,
}: {
  params: { gift_id: string };
}) {
  const { t } = useI18n();
  const share = useShare();
  const { share_url } = useShareUrl();
  const { data: gift } = useQuery<Gift>('user_gifts', {
    params: {
      id: params.gift_id,
    },
  });

  // Calculate max $MOONINI from gift.total (total amount in base units)
  const maxShareGiftMoonini = useMemo(() => {
    if (!gift?.total) return '0';
    return new BigNumber(gift.total)
      .dividedBy(new BigNumber(10).pow(MOONINI_DECIMALS))
      .toFixed(2);
  }, [gift]);

  // Calculate earned $MOONINI from gift data
  // earned = recipients * (settings[0].ca / 10^18)
  const earnedMoonini = useMemo(() => {
    if (!gift || !gift.metadata?.settings?.[0]?.ca) return '0';

    const claimAmount = gift.metadata.settings[0].ca; // Each claim amount in base units
    const recipients = gift.recipients || 0; // Number of claims

    // Calculate total earned: recipients * claim_amount
    const totalEarned = new BigNumber(claimAmount)
      .multipliedBy(recipients)
      .dividedBy(new BigNumber(10).pow(MOONINI_DECIMALS));

    return totalEarned.toFixed(2);
  }, [gift]);

  const progressPercentage = useMemo(() => {
    const earned = new BigNumber(earnedMoonini);
    const max = new BigNumber(maxShareGiftMoonini);
    if (max.eq(0)) return 0;
    return Math.min(earned.dividedBy(max).multipliedBy(100).toNumber(), 100);
  }, [earnedMoonini, maxShareGiftMoonini]);

  if (!gift) {
    return null;
  }

  return (
    <div className="flex flex-col justify-start w-full h-full gap-2 py-3 px-5">
      <img
        src="/images/share-gift.jpg"
        alt={gift.description}
        className="bg-card object-contain w-full aspect-640/360 rounded-lg overflow-hidden"
      />
      <div className="flex flex-col justify-start w-full flex-1 gap-2">
        {/* $MOONINI Progress Card */}
        <div className="card flex flex-col justify-start w-full gap-2">
          {/* <div className="flex items-center justify-between">
            <Typography size="2" weight={600}>
              {t('gift.share_gift_progress', { earned: earnedMoonini, max: maxShareGiftMoonini })}
            </Typography>
            <Typography size="2" weight={500} className="text-muted-foreground">
              {progressPercentage.toFixed(1)}%
            </Typography>
          </div> */}

          {/* Progress Bar */}
          {/* <div className="w-full h-3 rounded-full overflow-hidden relative" style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)' }}>
            <div
              className="h-full transition-all duration-300 ease-out rounded-full relative z-10"
              style={{ width: `${progressPercentage}%`, backgroundColor: '#ef4444' }}
            />
          </div> */}



          <Typography size="2" whitespacePreWrap overflowWrap="break-word">
            {t('gift.share_gift_description')}
          </Typography>

          <Typography size="1" color="rgba(255,255,255,0.54)" className="mt-1">
            {t('gift.fission_limit_note')}
          </Typography>
        </div>

        <div className="w-full mt-auto pb-2">
          <Button
            width="full"
            color="primary"
            variant="contained"
            size="sm"
            rounded="full"
            onClick={() => {
              if (share_url) {
                share({
                  url: share_url,
                  text: t('share.text'),
                });
              }
            }}
          >
            {t('common.share')}
          </Button>
        </div>
      </div>
    </div>
  );
}
